import { useForm } from "react-hook-form";
import styled from "@emotion/styled";

import { Select } from "components/form_components/Inputs";
import { useFormAutoSubmit } from "lib/hooks/useFormAutoSubmit";
import { SpendView } from "./hooks/useSpendUrlState";

/**
 * The pivot reads as the name of the view rather than a grouping, because that is what
 * choosing it does: it swaps the chart and the table under it.
 */
const GROUP_BY_OPTIONS = [
  { label: "Trend over time", value: "time" },
  { label: "Buyer rankings", value: "buyers" },
  { label: "Supplier rankings", value: "suppliers" },
  { label: "Buyer type rankings", value: "buyerTypes" },
];

const MEASURE_OPTIONS = [
  { label: "By transaction value", value: "totalValue" },
  { label: "By transaction count", value: "transactionCount" },
];

/** Only the ranking charts have a choice: the trend over time is bars plus a count line. */
const CHART_TYPE_OPTIONS = [
  { label: "Stacked area", value: "stackedArea" },
  { label: "Stacked bar", value: "stackedBar" },
  { label: "Lines", value: "line" },
];

const INTERVAL_OPTIONS = [
  { label: "Monthly", value: "month" },
  { label: "Quarterly", value: "quarter" },
  { label: "Yearly", value: "year" },
];

type ControlValues = Pick<SpendView, "trendsGroupBy" | "measure" | "interval" | "chartType">;

type Props = {
  view: SpendView;
  /** Called with only the field that changed, so the rest of the view is untouched. */
  onChange: (update: Partial<SpendView>) => void;
};

/**
 * How the transactions are read in the Trends view. These sit apart from the filters: the
 * filters are what you're looking at, these are how you're looking at it.
 *
 * Read left to right: the question, then its presentation. The left pair names the view
 * and the measure it ranks by, which only make sense together. The right pair is the
 * shape and the bucket size. Only a ranking has a measure or a shape to choose.
 */
export function SpendTrendsControls({ view, onChange }: Props) {
  const values: ControlValues = {
    trendsGroupBy: view.trendsGroupBy,
    measure: view.measure,
    interval: view.interval,
    chartType: view.chartType,
  };
  const { control, watch, getValues, trigger } = useForm<ControlValues>({
    defaultValues: values,
    values,
  });

  useFormAutoSubmit({
    watch,
    getValues,
    trigger,
    provideMetadata: true,
    onChange: (data, { name }) => {
      if (name === "trendsGroupBy") onChange({ trendsGroupBy: data.trendsGroupBy });
      if (name === "measure") onChange({ measure: data.measure });
      if (name === "interval") onChange({ interval: data.interval });
      if (name === "chartType") onChange({ chartType: data.chartType });
    },
  });

  return (
    <Controls aria-label="Trends controls">
      <PivotControls>
        <Control>
          <Select
            name="trendsGroupBy"
            label=""
            aria-label="Trends view"
            control={control}
            options={GROUP_BY_OPTIONS}
            data-testid="spend-trends-grouped-by"
          />
        </Control>
        {view.trendsGroupBy !== "time" && (
          <Control>
            <Select
              name="measure"
              label=""
              aria-label="Measure"
              control={control}
              options={MEASURE_OPTIONS}
              data-testid="spend-trends-measure"
            />
          </Control>
        )}
      </PivotControls>
      <ViewControls>
        {view.trendsGroupBy !== "time" && (
          <Control>
            <Select
              name="chartType"
              label=""
              aria-label="Chart type"
              control={control}
              options={CHART_TYPE_OPTIONS}
              data-testid="spend-trends-chart-type"
            />
          </Control>
        )}
        <Control>
          <Select
            name="interval"
            label=""
            aria-label="Interval"
            control={control}
            options={INTERVAL_OPTIONS}
            data-testid="spend-interval"
          />
        </Control>
      </ViewControls>
    </Controls>
  );
}

const Controls = styled.div({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  // The two groups hold their own line each when the row runs out of room, rather than
  // the pairs inside them breaking up.
  flexWrap: "wrap",
  gap: 16,
});

/**
 * What is being charted: the view and, for a ranking, the measure it ranks by. The pair
 * stays on one line and shrinks when space is tight, rather than wrapping: a wrapping
 * flex container measures itself from its contents, which here is narrower than the two
 * dropdowns, so it would fold the measure under the view picker at any width.
 */
const PivotControls = styled.div(({ theme }) => ({
  display: "flex",
  gap: 16,
  // Holds its size against the presentation controls, which grow into whatever is left.
  // Without this the pair is squeezed until "By transaction value" truncates.
  flexShrink: 0,
  // Two dropdowns that neither shrink nor wrap need 416px, which is wider than a phone.
  // Below the tablet tier they stack instead, since the alternative is the page scrolling
  // sideways. Wrapping is off above it deliberately: a wrapping flex container measures
  // itself from its contents, which is narrower than the pair, so it would fold them at
  // any width.
  [theme.breakpoints.mobileOnly]: {
    flexWrap: "wrap",
    // Wrapping alone doesn't bite: a container that can't shrink keeps the width its
    // children need on one line, so it never reaches a width that would wrap them.
    flexShrink: 1,
  },
}));

/**
 * How it is drawn. Takes the space the pivot controls leave rather than sizing to its
 * contents, so its dropdowns lay out against the room they actually have, and wraps when
 * they don't fit beside the pivot pair and the filter panel.
 */
const ViewControls = styled.div({
  display: "flex",
  flex: 1,
  flexWrap: "wrap",
  justifyContent: "flex-end",
  gap: 16,
});

const Control = styled.div({
  // A fixed 200, so every dropdown reads as the same control and the longest option
  // ("By transaction value") always fits. Shrinking is what truncates it, and the groups
  // wrap before the dropdowns inside them give way.
  flex: "0 0 200px",
  // antd sizes a select to its current value, so without this each one is a different
  // width and the row reflows as you change a selection.
  "& .ant5-select": {
    width: "100%",
  },
});
